// Station icon SVGs (rounded, no arrow) - for UI components (sidebar, popups)
import stationsIcon50 from '@/assets/icons/competitors-icons/stations_50.svg?raw';
import stationsIconL from '@/assets/icons/competitors-icons/stations_L.svg?raw';
import stationsIconM from '@/assets/icons/competitors-icons/stations_M.svg?raw';
import stationsIconS from '@/assets/icons/competitors-icons/stations_S.svg?raw';
import stationsIconXL from '@/assets/icons/competitors-icons/stations_XL.svg?raw';
import stationsIconXS from '@/assets/icons/competitors-icons/stations_XS.svg?raw';
import stationsIconXXL from '@/assets/icons/competitors-icons/stations_XXL.svg?raw';
import baseMarkerCompetitor from '@/assets/icons/competitors-markers/base-marker-competitor.svg?raw';
// Station marker SVGs (with bottom arrow) - for Mapbox map icons
import stationsMarker50 from '@/assets/icons/competitors-markers/stations_50.svg?raw';
import stationsMarkerL from '@/assets/icons/competitors-markers/stations_L.svg?raw';
import stationsMarkerM from '@/assets/icons/competitors-markers/stations_M.svg?raw';
import stationsMarkerS from '@/assets/icons/competitors-markers/stations_S.svg?raw';
import stationsMarkerXL from '@/assets/icons/competitors-markers/stations_XL.svg?raw';
import stationsMarkerXS from '@/assets/icons/competitors-markers/stations_XS.svg?raw';
import stationsMarkerXXL from '@/assets/icons/competitors-markers/stations_XXL.svg?raw';

import { createColoredMarkerSvg } from '../utils/createColoredMarkerSvg';

/**
 * Station charge range keys matching STATION_COLORS in constants
 */
export type StationRangeKey =
  | 'DEFAULT'
  | 'RANGE_1_10'
  | 'RANGE_10_15'
  | 'RANGE_15_20'
  | 'RANGE_20_25'
  | 'RANGE_25_30'
  | 'RANGE_30_50'
  | 'RANGE_50_PLUS';

/**
 * Station icon configuration containing both marker (for map) and icon (for UI) SVGs
 */
export interface StationIconConfig {
  /** Full marker SVG with bottom arrow for Mapbox map */
  marker: string;
  /** Rounded icon SVG without arrow for UI components */
  icon: string;
}

// Default color for stations without charge data
const DEFAULT_COLOR = '#CCCCCC';

/**
 * Map of station range keys to their corresponding SVG assets.
 * Use this to get the appropriate icon/marker for each charge range.
 */
export const STATION_ICONS: Record<StationRangeKey, StationIconConfig> = {
  DEFAULT: {
    marker: createColoredMarkerSvg(baseMarkerCompetitor, DEFAULT_COLOR),
    icon: createColoredMarkerSvg(baseMarkerCompetitor, DEFAULT_COLOR), // Fallback to base marker
  },
  RANGE_1_10: {
    marker: stationsMarkerXS,
    icon: stationsIconXS,
  },
  RANGE_10_15: {
    marker: stationsMarkerS,
    icon: stationsIconS,
  },
  RANGE_15_20: {
    marker: stationsMarkerM,
    icon: stationsIconM,
  },
  RANGE_20_25: {
    marker: stationsMarkerL,
    icon: stationsIconL,
  },
  RANGE_25_30: {
    marker: stationsMarkerXL,
    icon: stationsIconXL,
  },
  RANGE_30_50: {
    marker: stationsMarkerXXL,
    icon: stationsIconXXL,
  },
  RANGE_50_PLUS: {
    marker: stationsMarker50,
    icon: stationsIcon50,
  },
};

/** Logical size of a station map pin. Electra and competitors share it. */
export const STATION_MARKER_WIDTH = 32;
export const STATION_MARKER_HEIGHT = 36;

const ELECTRA_MARKER_SLUG: Record<StationRangeKey, string> = {
  DEFAULT: 'default',
  RANGE_1_10: '1-10',
  RANGE_10_15: '10-15',
  RANGE_15_20: '15-20',
  RANGE_20_25: '20-25',
  RANGE_25_30: '25-30',
  RANGE_30_50: '30-50',
  RANGE_50_PLUS: '50-plus',
};

/** Mapbox image id for an Electra station in this charge band. */
export function electraMarkerId(rangeKey: StationRangeKey): string {
  return `marker-electra-${ELECTRA_MARKER_SLUG[rangeKey]}`;
}

/** Competitor teardrop, navy head, bolt filled with the charge-band color. */
export function electraMarkerSvg(color: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 28 31"><g filter="url(#a)"><path fill="#fff" d="M16.696 25.432c-1.453 1.424-3.937 1.424-5.39 0L5.503 19.5c-2.002-2.001-.905-7 3.093-7h10.812c3.998 0 5.094 5 3.094 7z"/><rect width="20" height="20" x="4" y="2.5" fill="#0B2936" rx="10"/><rect width="20" height="20" x="4" y="2.5" stroke="#fff" stroke-width="2" rx="10"/><path fill="${color}" transform="translate(14 12.5) scale(0.58) translate(-18 -17)" d="m18.727 9.145-7.273 9.426H18l-.726 6.284 7.273-9.426H18l.726-6.284Z"/></g><defs><filter id="a" width="28" height="31" x="0" y="0" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/><feOffset dy="1.5"/><feGaussianBlur stdDeviation="1.5"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0"/><feBlend in2="BackgroundImageFix" result="effect1_dropShadow_11509_5093"/><feBlend in="SourceGraphic" in2="effect1_dropShadow_11509_5093" result="shape"/></filter></defs></svg>`;
}

/** Circle-only chip icons: the map markers' pins and shadows read as noise at chip size. */
export const ELECTRA_CHIP_ICON = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 22 22">
  <rect width="20" height="20" x="1" y="1" fill="#0B2936" rx="10"/>
  <rect width="20" height="20" x="1" y="1" stroke="#fff" stroke-width="2" rx="10"/>
  <path fill="#43F5BA" transform="translate(11 11) scale(0.8) translate(-18 -17)" d="m18.727 9.145-7.273 9.426H18l-.726 6.284 7.273-9.426H18l.726-6.284Z"/>
</svg>`;
export const LOW_HPC_CHIP_ICON = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 22 22">
  <rect width="20" height="20" x="1" y="1" fill="#8A9391" rx="10"/>
  <rect width="20" height="20" x="1" y="1" stroke="#fff" stroke-width="2" rx="10"/>
  <rect width="2" height="10" x="10" y="6" fill="#fff" rx="1"/>
</svg>`;

/**
 * Get the station icon configuration for a given range key
 */
export function getStationIconByRange(rangeKey: StationRangeKey): StationIconConfig {
  return STATION_ICONS[rangeKey];
}
